"use client";

import { motion, AnimatePresence } from "framer-motion";
import { useState } from "react";

const ALL_SLOTS = [
  "10:00 AM", "10:30 AM", "11:00 AM", "11:30 AM",
  "12:00 PM", "2:00 PM", "2:30 PM", "3:00 PM",
  "3:30 PM", "4:00 PM", "4:30 PM", "5:00 PM",
];

function getDaysInMonth(year: number, month: number) {
  return new Date(year, month + 1, 0).getDate();
}
function getFirstDayOfMonth(year: number, month: number) {
  return new Date(year, month, 1).getDay();
}
function toDateStr(year: number, month: number, day: number) {
  return `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

const monthNames = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const dayNames = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];

type Step = "calendar" | "form" | "confirmed";

export default function BookCall() {
  const today = new Date();
  const [currentMonth, setCurrentMonth] = useState(today.getMonth());
  const [currentYear, setCurrentYear] = useState(today.getFullYear());
  const [selectedDate, setSelectedDate] = useState<number | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);

  const [step, setStep] = useState<Step>("calendar");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const isCurrentMonth = currentMonth === today.getMonth() && currentYear === today.getFullYear();
  const daysInMonth = getDaysInMonth(currentYear, currentMonth);
  const firstDay = getFirstDayOfMonth(currentYear, currentMonth);


  const prevMonth = () => {
    if (isCurrentMonth) return;
    if (currentMonth === 0) { setCurrentMonth(11); setCurrentYear((y) => y - 1); }
    else setCurrentMonth((m) => m - 1);
    setSelectedDate(null);
    setSelectedTime(null);
  };

  const nextMonth = () => {
    if (currentMonth === 11) { setCurrentMonth(0); setCurrentYear((y) => y + 1); }
    else setCurrentMonth((m) => m + 1);
    setSelectedDate(null);
    setSelectedTime(null);
  };

  const isWeekend = (day: number) => {
    const dow = new Date(currentYear, currentMonth, day).getDay();
    return dow === 0 || dow === 6;
  };
  const isPast = (day: number) => isCurrentMonth && day <= today.getDate();

  const handleSubmit = async () => {
    if (!selectedDate || !selectedTime || !name.trim() || !email.trim()) return;
    setSubmitting(true);
    setSubmitError("");

    const subject = encodeURIComponent("Discovery Call Booking Request");
    const body = encodeURIComponent(
      `Name: ${name.trim()}\nEmail: ${email.trim()}\nPhone: ${phone.trim()}\nDate: ${selectedDateStr}\nTime: ${selectedTime}\n\nNotes:\n${notes.trim()}`
    );
    window.location.href = `mailto:thedevcubix@gmail.com?subject=${subject}&body=${body}`;
    setStep("confirmed");
    setSubmitting(false);
  };

  const selectedDateStr = selectedDate
    ? `${monthNames[currentMonth]} ${selectedDate}, ${currentYear}`
    : "";

  if (step === "confirmed") {
    return (
      <section className="py-12 sm:py-16 md:py-20 bg-surface relative overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-175 h-175 rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, rgba(225,29,36,0.06) 0%, transparent 70%)" }} />
        <div className="max-w-150 mx-auto px-6 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, ease: [0.25, 1, 0.5, 1] }}
          >
            <div className="w-16 h-16 rounded-full bg-[#22c55e]/15 border border-[#22c55e]/30 flex items-center justify-center mx-auto mb-6">
              <svg className="w-8 h-8 text-[#22c55e]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
              </svg>
            </div>
            <h2 className="text-3xl font-bold text-foreground mb-3 font-ui">You&apos;re Booked!</h2>
            <p className="text-foreground/50 mb-2 font-body">
              <span className="font-semibold text-foreground">{selectedDateStr}</span> at <span className="font-semibold text-foreground">{selectedTime} PKT</span>
            </p>
            <p className="text-foreground/40 text-sm mb-8">A confirmation has been sent to <span className="text-foreground/70">{email}</span>. We&apos;ll send a Google Meet link before the call.</p>
            <div className="inline-flex items-center gap-3 px-5 py-3 rounded-xl text-sm text-foreground/50" style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)" }}>
              <svg className="w-4 h-4 text-red-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
              30 min &bull; Google Meet &bull; $30 billed at the call
            </div>
          </motion.div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-12 sm:py-16 md:py-20 bg-surface relative overflow-hidden">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-175 h-175 rounded-full pointer-events-none" style={{ background: "radial-gradient(circle, rgba(225,29,36,0.06) 0%, transparent 70%)" }} />

      <div className="max-w-275 mx-auto px-6 relative z-10">
        {/* Header */}
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <p className="section-badge mb-4">Book a Call</p>
          <h2 className="text-2xl sm:text-3xl md:text-[48px] font-bold leading-[1.2] font-ui mb-4">
            <span>Schedule a </span>
            <span className="font-serif italic text-purple-light">Discovery Call</span>
          </h2>
          <p className="text-foreground/50 max-w-xl mx-auto font-body">
            30 minutes. We&apos;ll discuss your project, recommend the right approach, and give you a clear roadmap.
          </p>
        </motion.div>

        <motion.div
          className="max-w-215 mx-auto"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.25, 1, 0.5, 1] }}
          viewport={{ once: true }}
        >
          <AnimatePresence mode="wait">
            {step === "calendar" && (
              <motion.div
                key="calendar"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
              >
                <div className="bg-surface-light rounded-2xl border border-card-border overflow-hidden">
                  <div className="grid md:grid-cols-[1fr_280px]">
                    {/* Calendar */}
                    <div className="p-6 md:p-8">
                      <div className="flex items-center justify-between mb-6">
                        <button onClick={prevMonth} disabled={isCurrentMonth} className="w-10 h-10 rounded-full flex items-center justify-center text-foreground/50 hover:text-foreground hover:bg-foreground/5 transition-colors disabled:opacity-20 disabled:cursor-not-allowed">
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
                        </button>
                        <h3 className="text-lg font-bold font-ui">{monthNames[currentMonth]} {currentYear}</h3>
                        <button onClick={nextMonth} className="w-10 h-10 rounded-full flex items-center justify-center text-foreground/50 hover:text-foreground hover:bg-foreground/5 transition-colors">
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
                        </button>
                      </div>

                      <div className="grid grid-cols-7 gap-1 mb-2">
                        {dayNames.map((d) => <div key={d} className="text-center text-xs font-medium text-foreground/30 py-1">{d}</div>)}
                      </div>

                      <div className="grid grid-cols-7 gap-1">
                        {Array.from({ length: firstDay }).map((_, i) => <div key={`e-${i}`} />)}
                        {Array.from({ length: daysInMonth }).map((_, i) => {
                          const day = i + 1;
                          const disabled = isPast(day) || isWeekend(day);
                          const selected = selectedDate === day;
                          return (
                            <button
                              key={day}
                              disabled={disabled}
                              onClick={() => { setSelectedDate(day); setSelectedTime(null); }}
                              className={`aspect-square rounded-lg text-sm font-medium transition-all duration-200
                                ${disabled ? "text-foreground/15 cursor-not-allowed" : "text-foreground/70 hover:bg-foreground/5 hover:text-foreground cursor-pointer"}
                                ${selected ? "bg-red-accent! text-white! ring-2 ring-red-accent/30" : ""}
                              `}
                            >
                              {day}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Time slots */}
                    <div className="border-t md:border-t-0 md:border-l border-card-border p-6 md:p-5 bg-subtle-bg">
                      <p className="text-xs font-medium text-foreground/30 uppercase tracking-wider mb-4">
                        {selectedDate ? `${monthNames[currentMonth]} ${selectedDate}` : "Select a date"}
                      </p>

                      {selectedDate ? (
                        <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                          {ALL_SLOTS.map((time) => {
                            const selected = selectedTime === time;
                            return (
                              <button
                                key={time}
                                onClick={() => setSelectedTime(time)}
                                className={`w-full px-4 py-3 rounded-lg text-sm font-medium text-left transition-all duration-200
                                  ${selected ? "bg-red-accent text-white" : "text-foreground/60 hover:bg-foreground/5 hover:text-foreground border border-card-border"}
                                `}
                              >
                                {time}
                              </button>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="flex items-center justify-center h-56">
                          <p className="text-foreground/20 text-sm text-center">Pick a date to see<br />available time slots</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Bottom bar */}
                  <div className="border-t border-card-border px-6 md:px-8 py-5 flex flex-col sm:flex-row items-center justify-between gap-4">
                    <div className="flex items-center gap-4 text-sm text-foreground/50">
                      <span className="flex items-center gap-1.5">
                        <svg className="w-4 h-4 text-red-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                        30 min
                      </span>
                      <span className="flex items-center gap-1.5">
                        <svg className="w-4 h-4 text-red-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 10.5l4.72-4.72a.75.75 0 011.28.53v11.38a.75.75 0 01-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 002.25-2.25v-9a2.25 2.25 0 00-2.25-2.25h-9A2.25 2.25 0 002.25 7.5v9a2.25 2.25 0 002.25 2.25z" /></svg>
                        Google Meet
                      </span>
                      <span className="flex items-center gap-1.5">
                        <svg className="w-4 h-4 text-red-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                        $30
                      </span>
                    </div>
                    <button
                      disabled={!selectedDate || !selectedTime}
                      onClick={() => setStep("form")}
                      className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold text-white transition-all duration-300 disabled:opacity-30 disabled:cursor-not-allowed hover:shadow-lg hover:shadow-red-accent/20"
                      style={{ background: "linear-gradient(135deg, #E11D24, #B91620)" }}
                    >
                      Continue
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" /></svg>
                    </button>
                  </div>
                </div>
              </motion.div>
            )}

            {step === "form" && (
              <motion.div
                key="form"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ duration: 0.3 }}
              >
                <div className="bg-surface-light rounded-2xl border border-card-border overflow-hidden">
                  {/* Selected slot summary */}
                  <div className="px-6 md:px-8 py-5 border-b border-card-border flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-lg bg-red-accent/10 flex items-center justify-center">
                        <svg className="w-4 h-4 text-red-accent" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" /></svg>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-foreground">{selectedDateStr} &bull; {selectedTime} PKT</p>
                        <p className="text-xs text-foreground/40">30 min Discovery Call &bull; Google Meet</p>
                      </div>
                    </div>
                    <button onClick={() => { setStep("calendar"); setSubmitError(""); }} className="text-xs text-foreground/40 hover:text-foreground transition-colors underline underline-offset-2">
                      Change
                    </button>
                  </div>

                  <div className="p-6 md:p-8 grid md:grid-cols-2 gap-5">
                    <div className="flex flex-col gap-1.5">
                      <label className="text-xs font-medium text-foreground/50 uppercase tracking-wide">Full Name *</label>
                      <input
                        value={name}
                        onChange={e => setName(e.target.value)}
                        placeholder="Nabeel Imdad"
                        className="px-4 py-3 rounded-xl bg-surface border border-card-border text-sm text-foreground placeholder-foreground/25 focus:outline-none focus:border-red-accent/50 transition-colors"
                      />
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <label className="text-xs font-medium text-foreground/50 uppercase tracking-wide">Email *</label>
                      <input
                        type="email"
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                        placeholder="you@company.com"
                        className="px-4 py-3 rounded-xl bg-surface border border-card-border text-sm text-foreground placeholder-foreground/25 focus:outline-none focus:border-red-accent/50 transition-colors"
                      />
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <label className="text-xs font-medium text-foreground/50 uppercase tracking-wide">Phone / WhatsApp</label>
                      <input
                        value={phone}
                        onChange={e => setPhone(e.target.value)}
                        placeholder="+1 234 567 8900"
                        className="px-4 py-3 rounded-xl bg-surface border border-card-border text-sm text-foreground placeholder-foreground/25 focus:outline-none focus:border-red-accent/50 transition-colors"
                      />
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <label className="text-xs font-medium text-foreground/50 uppercase tracking-wide">What do you need?</label>
                      <input
                        value={notes}
                        onChange={e => setNotes(e.target.value)}
                        placeholder="e.g. Pitch deck for Series A, 20 slides"
                        className="px-4 py-3 rounded-xl bg-surface border border-card-border text-sm text-foreground placeholder-foreground/25 focus:outline-none focus:border-red-accent/50 transition-colors"
                      />
                    </div>
                  </div>

                  <div className="border-t border-card-border px-6 md:px-8 py-5 flex items-center justify-between gap-4">
                    <p className="text-xs text-foreground/30">Payment of $30 collected at the start of the call.</p>
                    <div className="flex items-center gap-3">
                      {submitError && <p className="text-xs text-red-400">{submitError}</p>}
                      <button
                        disabled={!name.trim() || !email.trim() || submitting}
                        onClick={handleSubmit}
                        className="inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold text-white transition-all duration-300 disabled:opacity-40 disabled:cursor-not-allowed hover:shadow-lg hover:shadow-red-accent/20"
                        style={{ background: "linear-gradient(135deg, #E11D24, #B91620)" }}
                      >
                        {submitting ? (
                          <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Booking...</>
                        ) : (
                          <>Confirm Booking</>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
}
